import { useSearchParams } from "react-router";
import { type RowClickedEvent } from "ag-grid-community";
import { ListIllustration } from "vega-illustrations";

import { type LimitedPartnerDealActivity } from "@vega-api/generated/types/limitedPartnerDealActivity";
import { DataGrid } from "@vega-core/components/DataGrid/DataGrid";
import {
  DATA_GRID_HEADER_HEIGHT,
  DATA_GRID_ROW_HEIGHT,
} from "@vega-core/components/DataGrid/DataGridProvider";
import {
  type SegmentedTab,
  SegmentedTabs,
} from "@vega-core/components/SegmentedTabs/SegmentedTabs";
import { m } from "@vega-core/paraglide/messages";

import { useDealActivityColumns } from "../../hooks/useDealActivityColumns";
import { bucketDealsByStatus } from "../../utils/bucketDealsByStatus";

export const DEAL_TAB_PARAM = "dealTab";

const MAX_AUTO_HEIGHT_ROWS = 10;
const SCROLLABLE_GRID_HEIGHT =
  DATA_GRID_HEADER_HEIGHT + MAX_AUTO_HEIGHT_ROWS * DATA_GRID_ROW_HEIGHT;

const DealTab = {
  Committed: "committed",
  Prospecting: "prospecting",
  Passed: "passed",
} as const;

type DealTab = (typeof DealTab)[keyof typeof DealTab];

const isDealTab = (value: string | null): value is DealTab =>
  value === DealTab.Committed ||
  value === DealTab.Prospecting ||
  value === DealTab.Passed;

type DealActivityListProps = {
  deals: LimitedPartnerDealActivity[];
  onViewDeal: (deal: LimitedPartnerDealActivity) => void;
  showPassedTab?: boolean;
  passedDealsNavigable?: boolean;
  isLoading?: boolean;
};

export const DealActivityList = ({
  deals,
  onViewDeal,
  showPassedTab = true,
  passedDealsNavigable = true,
  isLoading,
}: DealActivityListProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const paramTab = searchParams.get(DEAL_TAB_PARAM);
  const requestedTab: DealTab = isDealTab(paramTab)
    ? paramTab
    : DealTab.Committed;
  const activeTab: DealTab =
    requestedTab === DealTab.Passed && !showPassedTab
      ? DealTab.Committed
      : requestedTab;

  const buckets = bucketDealsByStatus(deals);
  const activeRows = buckets[activeTab];
  const isScrollable = activeRows.length > MAX_AUTO_HEIGHT_ROWS;
  const rowsNavigable = activeTab !== DealTab.Passed || passedDealsNavigable;

  const columnDefs = useDealActivityColumns();

  const tabs: SegmentedTab<DealTab>[] = [
    {
      id: DealTab.Committed,
      label: m.coInvestments_insights_lpProfile_deals_tab_committed({
        count: buckets.committed.length,
      }),
    },
    {
      id: DealTab.Prospecting,
      label: m.coInvestments_insights_lpProfile_deals_tab_prospecting({
        count: buckets.prospecting.length,
      }),
    },
    ...(showPassedTab
      ? [
          {
            id: DealTab.Passed,
            label: m.coInvestments_insights_lpProfile_deals_tab_passed({
              count: buckets.passed.length,
            }),
          },
        ]
      : []),
  ];

  const setActiveTab = (tab: DealTab) => {
    const next = new URLSearchParams(searchParams);
    next.set(DEAL_TAB_PARAM, tab);
    setSearchParams(next, { replace: true });
  };

  const handleRowClicked = (
    event: RowClickedEvent<LimitedPartnerDealActivity>,
  ) => {
    if (!event.data || !rowsNavigable) return;
    onViewDeal(event.data);
  };

  return (
    <div className="flex flex-col gap-4" data-testid="deal-activity-list">
      <SegmentedTabs<DealTab>
        activeTab={activeTab}
        fullWidth
        setActiveTab={setActiveTab}
        tabs={tabs}
      />
      <DataGrid<LimitedPartnerDealActivity>
        columnDefs={columnDefs}
        containerStyle={
          isScrollable ? { height: SCROLLABLE_GRID_HEIGHT } : undefined
        }
        domLayout={isScrollable ? "normal" : "autoHeight"}
        emptyState={{
          title: m.coInvestments_insights_lpProfile_deals_empty(),
          illustration: <ListIllustration />,
        }}
        loading={isLoading}
        onRowClicked={handleRowClicked}
        rowClass={rowsNavigable ? "cursor-pointer" : undefined}
        rowData={activeRows}
      />
    </div>
  );
};
